/**
 * Zeitkonto (working-time account) hooks using TanStack Query.
 *
 * Provides the per-employee current + projected overtime balance for a planner
 * window. Mirrors the conditional-fetch pattern used for group coverage: the
 * caller controls an `enabled` flag so the projection is only fetched in
 * week/day view + byEmployee mode.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { WorkforceService } from '@/api/services/WorkforceService';
import type { ZeitkontoProjection } from '@/types/zeitkonto';

// Query keys for Zeitkonto projections
export const zeitkontoKeys = {
  all: ['zeitkonto'] as const,
  projections: () => [...zeitkontoKeys.all, 'projection'] as const,
  projection: (fromDate: string, toDate: string, employeeIds?: string[]) =>
    [
      ...zeitkontoKeys.projections(),
      { fromDate, toDate, employeeIds: employeeIds ?? null },
    ] as const,
};

export interface UseZeitkontoProjectionParams {
  /** Window start date (YYYY-MM-DD) */
  fromDate: string;
  /** Window end date (YYYY-MM-DD) */
  toDate: string;
  /** Optional list of employee IDs to scope the projection to */
  employeeIds?: string[];
  /**
   * Caller-controlled gate. Must be true only in week/day view + byEmployee
   * mode; the query never fires when false.
   */
  enabled?: boolean;
}

/**
 * Normalize a generated projection item into a strict ZeitkontoProjection,
 * dropping any entry without an employee_id (cannot be keyed) and defaulting
 * missing numbers to 0.
 */
function normalizeProjection(item: {
  employee_id?: string;
  current_hours?: number;
  planned_hours?: number;
  target_hours?: number;
  projected_hours?: number;
}): ZeitkontoProjection | null {
  if (!item.employee_id) return null;
  return {
    employee_id: item.employee_id,
    current_hours: item.current_hours ?? 0,
    planned_hours: item.planned_hours ?? 0,
    target_hours: item.target_hours ?? 0,
    projected_hours: item.projected_hours ?? 0,
  };
}

/**
 * Hook to fetch the Zeitkonto projection for a planner window.
 *
 * Returns the TanStack Query result extended with:
 * - `byEmployee`: a Record keyed by employee_id for O(1) lookup
 * - `projections`: the normalized array
 *
 * @param params - Window dates, optional employee scope, and enabled flag
 * @returns Query result with `byEmployee` map and `projections` array
 */
export function useZeitkontoProjection({
  fromDate,
  toDate,
  employeeIds,
  enabled = true,
}: UseZeitkontoProjectionParams) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  const query = useQuery({
    queryKey: zeitkontoKeys.projection(fromDate, toDate, employeeIds),
    queryFn: async () => {
      const employeeIdsParam =
        employeeIds && employeeIds.length > 0 ? employeeIds.join(',') : '';
      const response = await WorkforceService.getApiV1WorkforceZeitkontoProjection(
        fromDate,
        toDate,
        employeeIdsParam
      );
      const projections: ZeitkontoProjection[] = [];
      for (const item of response.projections ?? []) {
        const normalized = normalizeProjection(item);
        if (normalized) projections.push(normalized);
      }
      return projections;
    },
    enabled: enabled && !!fromDate && !!toDate && isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on authentication/authorization errors
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const byEmployee = useMemo(() => {
    const map: Record<string, ZeitkontoProjection> = {};
    for (const projection of query.data ?? []) {
      map[projection.employee_id] = projection;
    }
    return map;
  }, [query.data]);

  return {
    ...query,
    projections: query.data ?? [],
    byEmployee,
  };
}
